<script>
	import { getContext, onMount } from 'svelte'

	import DraggableComponent from '@Components/DraggableComponent.svelte'
	import ResizeableComponent from '@Components/ResizeableComponent.svelte'
	import RenderSettings from './RenderSettings.svelte'
	import LoadingIndicator from '@Components/LoadingIndicator.svelte'
	import { getRenderProperties, setRenderProperties } from '@Utils/Properties.js'
	import { getElementFromFeature } from '@Utils/Elements.js'
	import { deconstructHoleId } from '@Utils/DataController.js'

	const { getEditorCanvas, getController, getRenderer, getEditorState } = getContext('editor')
	const editorCanvas = getEditorCanvas()
	const controller = getController()

	const minQuality = 1
	const maxQuality = 5
	let showAdvanced = false
	let showTeeMarkers = localStorage.getItem('previewTeeMarkers') === 'false' ? false : true
	let showShadows = localStorage.getItem('previewShadows') === 'false' ? false : true
	let autoRender = localStorage.getItem('previewAutoRender') === 'true' ? true : false
	let previewQuality = getQuality()
	let focusedData = null
	let updates = []
	let isVisible = false
	let holeRenderer = getRenderer()
	let editorState = getEditorState()
	let selectedData
	let renderElement
	let renderOverlay
	let currentImage = null
	let zoomView
	let componentView
	let timer
	let renderType = 'hole'
	let renderProperties = getCurrentProperties()
	let isLoading = false
	let mustReset = true
	let previousMoueMove = null
	let isMouseOver = false
	let isRendererOwner = true
	$: isDarkMode = window.isDarkMode
	$: showShadows, updateShadow()
	$: showZoomView = false
	$: showTeeMarkers, toggleShowTeeMarkers()
	$: autoRender, updateAutoRender()
	$: renderProperties
	$: aspectRatio = renderProperties.width / renderProperties.height
	$: currentStatus = ''
	holeRenderer.on('status', (status) => {
		if(!isVisible || !isRendererOwner) {
			currentStatus = ''
			return
		}
		if (status == 'Done') {
			currentStatus = ''
		} else {
			currentStatus = status
		}
	})
	function _getContext(canvas) {
		if (canvas.getContext) {
			return canvas.getContext('2d')
		} else {
			return null
		}
	}
	editorState.on('rendererOwnerChanged', (owner) => {
		if(owner === "preview") {
			isRendererOwner = true
		} else {
			isRendererOwner = false
		}
		if(isRendererOwner === false) {
			mustReset = true
		}
	})
	window.addEventListener('colorSchemeChanged', (e) => {
		isDarkMode = e.isDarkMode
	})
	controller.on('settingsChanged', function () {
		mustReset = true
		if (renderType === 'course') {
			renderProperties = getCurrentProperties()
			renderImage()
		} else {
			renderProperties = getCurrentProperties()
			renderImage()
		}
	})
	function getQuality() {
		let stored = parseInt(localStorage.getItem('previewQuality'), 10)
		if (stored && stored >= minQuality && stored <= maxQuality) {
			return stored
		}
		return 1
	}
	function update() {}
	function getTheme() {
		let name = 'park'
		let pq = parseInt(previewQuality)
		if (renderType === 'course') {
			return name + 'Course'
		}
		if (focusedData) {
			let h = deconstructHoleId(focusedData.feature.get('holeId'))
			if (h) {
				name = controller.getGroupSetting(h.holeGroupId, 'theme') ?? 'park'
			}
		}
		if (pq === maxQuality) {
			return name
		}
		return name + 'Preview'
	}
	function getCurrentProperties() {
		const pq = parseInt(previewQuality, 10)
		const quality = maxQuality - pq + 1
		const shadowMapSizes = {
			q1: 256,
			q2: 512,
			q3: 512,
			q4: 512,
			q5: 1024
		}
		const qualitySetting = {
			q1: "low",
			q2: "low",
			q3: "medium",
			q4: "medium",
			q5: "high"
		}
		const theme = getTheme()
		const props = getRenderProperties({
			includeTeeMarkers: showTeeMarkers,
			includeTexts: true,
			antialias: false,
			saveData: true,
			planeSegmentMultiplier: Math.max(pq,2)/maxQuality,
			postProcessing: pq === maxQuality,
			texturePack: theme,
			shadowIntensity: showShadows ? 0.9 : 0,
			shadowMapSize: shadowMapSizes['q' + pq],
			qualitySetting: qualitySetting['q' + pq],
		})
		if (renderType === 'hole') {
			props.width = Math.round(import.meta.env.VITE_RENDER_WIDTH / quality)
			props.height = Math.round(import.meta.env.VITE_RENDER_HEIGHT / quality)
			props.shadowIntensity = showShadows ? 0.9 : 0
			props.renderTarget = 'hole'
			props.includeIcons = true
			props.useSimplifiedObjects = pq === minQuality
			props.useAlphaMapForBuildingRoofs = pq === maxQuality
			props.watermarkScale = props.watermarkScale / quality
		} else if (renderType === 'course') {
			props.width = Math.round(import.meta.env.VITE_RENDER_HEIGHT / quality)
			props.height = Math.round(import.meta.env.VITE_RENDER_WIDTH / quality)
			props.includeIcons = true
			props.includeTexts = true
			props.largeForest = true
			props.renderTarget = 'course'
			props.includeTeeMarkers = false
			props.includeTeeColors = false
			props.includePlaylines = true
			props.shadowIntensity = showShadows ? 1.1 : 0
			props.watermarkScale = props.watermarkScale / quality
			props.useSimplifiedObjects = pq === minQuality
			props.useAlphaMapForBuildingRoofs = pq === maxQuality
			props.watermarkScale = props.watermarkScale / quality
		} else if (renderType === 'green') {
			props.width = Math.round(import.meta.env.VITE_RENDER_WIDTH / quality)
			props.height = Math.round(import.meta.env.VITE_RENDER_WIDTH / quality)
			props.shadowIntensity = showShadows ? 0.9 : 0
			props.renderTarget = 'green'
			props.watermarkScale = props.watermarkScale / quality
		}
		return props
	}
	function toggleShowTeeMarkers() {
		localStorage.setItem('previewTeeMarkers', showTeeMarkers)
		renderProperties = getCurrentProperties()
		updateImageProperties()
	}
	function updateRenderProperties(renderHole) {
		renderProperties = getCurrentProperties()
		mustReset = true
		let a = renderProperties.width / renderProperties.height
		if (a !== aspectRatio) {
			aspectRatio = a
		}
		if (!autoRender) { 
			return
		}
		if (renderHole) {
			renderImage()
		} else {
			updateImageProperties()
		}
	}
	function updateRenderType(type) {
		if (renderType === type) {
			return
		}
		renderType = type
		renderProperties = getCurrentProperties()
		const context = _getContext(renderElement)
		context.clearRect(0, 0, renderElement.width, renderElement.height)
		let a = renderProperties.width / renderProperties.height
		if (a !== aspectRatio) {
			aspectRatio = a
		}
	}
	editorCanvas.on('featureRemoved', (e) => {
		updateImage(e, 'delete')
	})
	editorCanvas.on('featureUpdated', (e, event) => {
		if (e.get('objectType') === 'frame') {
			mustReset = true
			renderImage()
		} else {
			updateImage(e, 'update')
		}
	})
	function previewQualityChanged(e) {
		previewQuality = e.target.value
		localStorage.setItem('previewQuality', previewQuality)
		updateRenderProperties(true)
	}
	function didPressRender() {
		if (mustReset) {
			renderImage(true)
		} else {
			updateImage(null, null, true)
		}
	}
	function updateAutoRender() {
		localStorage.setItem('previewAutoRender', autoRender)
	}
	function updateShadow() {
		localStorage.setItem('previewShadows', showShadows)
		renderProperties = getCurrentProperties()
		mustReset = true
		renderImage()
	}
	editorCanvas.on('featureAdded', (e) => {
		updateImage(e, 'add')
	})
	editorCanvas.on('frameFocused', function (data) {
		focusedData = data
		isVisible = focusedData !== null
		currentImage = null
		if (focusedData) {
			renderProperties = getCurrentProperties()
		}
	})
	async function updateImageProperties() {
		if (!isVisible || !isRendererOwner) {
			return
		}
		clearTimeout(timer)
		timer = setTimeout(async () => {
			if (selectedData) {
				isLoading = true
				try {
					let result = await holeRenderer.updateProperties(renderProperties)
					let context = renderElement
					renderElement.width = result.image.width
					renderElement.height = result.image.height
					context.drawImage(result.image, 0, 0)
					currentImage = result.image
				} catch (e) {
					console.error(e)
				}
				isLoading = false
			}
		}, 200)
	}
	async function updateImage(feature, action, runRender) {
		if (!isVisible || !isRendererOwner) {
			return
		}
		const shouldRender = runRender ?? autoRender
		if (shouldRender && mustReset) {
			renderImage(true)
			return
		}
		const checkIntersecting = ['terrain', 'manmade']
		if (feature) {
			const element = getElementFromFeature(feature)	
			let intersecting = []
			if(checkIntersecting.some(w => element.layers.includes(w))) {
				intersecting = editorCanvas.getFeaturesInExtent(feature.getGeometry().getExtent())
				intersecting = intersecting.filter((f) => {
					if (f.get('uid') === feature.get('uid')) {
						return false
					}
					if (f.get('objectType') === 'frame') {
						return false
					}
					if (f.get('elementName') === 'focus') {
						return false
					}
					if (f.get('hidden') === true) {
						return false
					}
					if (f.getGeometry().getType() !== 'Polygon') {
						return false
					}
					if (f.get('holeId') && f.get('holeId') !== focusedData.feature.get('holeId')) {
						return false
					}
					return true
				})
			}
			updates = updates.filter((u) => u.uid !== feature.get('uid'))
			updates.push({
				uid: feature.get('uid'),
				feature: editorCanvas.convertFeatureToGeoJSON(feature),
				action: action
			})
			for (let i of intersecting) {
				updates.push({
					uid: i.get('uid'),
					feature: editorCanvas.convertFeatureToGeoJSON(i),
					action: 'update'
				})
			}
		}
		if (!shouldRender) {
			return
		}
		clearTimeout(timer)
		timer = setTimeout(async () => {
			if (selectedData && updates.length > 0) {
				isLoading = true
				let completed = [...updates]
				try {
					let result = await holeRenderer.update(updates, renderProperties)
					updates = updates.filter((u) => !completed.includes(u))
					let context = _getContext(renderElement);
					renderElement.width = result.image.width
					renderElement.height = result.image.height
					context.drawImage(result.image, 0, 0)
					currentImage = result.image
					updateZoomImage()
				} catch (e) {
					console.error(e)
				}
				isLoading = false
			}
		}, 100)
	}
	async function renderImage(runRender) {
		if (!isVisible || !isRendererOwner) {
			return
		}
		
		let shouldRender = runRender ?? autoRender
		if(shouldRender === false) {
			return
		}
		currentImage = null
		clearTimeout(timer)
		timer = setTimeout(async () => {
			if (selectedData) {
				let reset = mustReset
				mustReset = false
				isLoading = true
				let holeId = selectedData.feature.get('holeId')
				let courseId = selectedData.feature.get('courseId')
				let data
				if (renderType === 'hole' && holeId) {
					data = editorCanvas.getHoleRenderData(holeId)
					//console.log('holeData', JSON.stringify(data))
				} else if (renderType === 'green' && holeId) {
					data = editorCanvas.getGreenRenderData(holeId)
					//console.log('greendata', data)
				} else if (courseId) {
					data = editorCanvas.getCourseRenderData(courseId, controller)
					//console.log('courseData', JSON.stringify(data))
				} else {
					console.error('No holeId or courseId found')
					return
				}
				try {
					let renderProperties = getCurrentProperties()
					let result = await holeRenderer.render(data, renderProperties, reset)
					let context = _getContext(renderElement);
					renderElement.width = result.image.width
					renderElement.height = result.image.height
					currentImage = result.image
					context.drawImage(result.image, 0, 0)
					updateZoomImage()
				} catch (e) {
					console.error(e)
				}
				isLoading = false
			}
		}, 100)
	}
	onMount(() => {
		update()
	})
	const onFrameFocused = (data) => {
		selectedData = data
		showAdvanced = import.meta.env.VITE_ADVANCED_TOOLING_ENABLED === 'true' && localStorage.getItem('showAdvancedOptions') === 'true'
		if (data) {
			if (data.feature.get('holeId')) {
				updateRenderType('hole')
			} else {
				updateRenderType('course')
			}
			mustReset = true
			renderImage()
		} else {
			const context = _getContext(renderElement);
			context.clearRect(0, 0, renderElement.width, renderElement.height)
		}
	}
	editorCanvas.on('frameFocused', onFrameFocused)
	function mouseover(e) {
		isMouseOver = true
		// if (showZoomView) {
		// 	return
		// }
		// if (!currentImage) {
		// 	return
		// }
		// let referenceBox = renderElement.parentNode.getBoundingClientRect()
		// if (referenceBox.width > currentImage.width) {
		// 	return
		// }
		// showZoomView = true
	}
	function updateZoomImage(e) {
		e = e ?? previousMoueMove
		if(!e) {
			showZoomView = false
			return
		}
		previousMoueMove = e
		if (!isMouseOver || !zoomView) {
			showZoomView = false
			return
		}
		if (!currentImage) {
			let context = _getContext(zoomView);
			context.clearRect(0, 0, zoomView.width, zoomView.height)
			showZoomView = false
			return
		}
		showZoomView = true
		let componentBox = renderElement.parentNode.parentNode.parentNode.getBoundingClientRect()
		//let componentBox = renderElement.parentNode.parentNode.getBoundingClientRect()
		let referenceBox = renderElement.parentNode.getBoundingClientRect()
		let ratio = renderProperties.width / renderProperties.height
		let bodyRect = document.body.getBoundingClientRect()
		let maxWidth = componentBox.x * 0.7 - 20

		let height = Math.min(bodyRect.height - 20, renderProperties.height)
		let width = Math.min(renderProperties.width, maxWidth)
		// let zoomYViewRatio = height / currentImage.height
		// let zoomXViewRatio = width / currentImage.width
		// console.log('zoomYViewRatio', zoomYViewRatio)
		// console.log('zoomXViewRatio', zoomXViewRatio)
		// //let diff = currentImage.width - width

		// // redo the whole component
		// // calculate a rectangle which shows the zoomed area. ie the max width and height that can be shown at the current position

		// let visibleX = renderProperties.width * zoomXViewRatio
		// let visibleY = renderProperties.height * zoomYViewRatio
		// let renderContext = renderElement.getContext('2d')
		// renderContext.strokeStyle = "red"
		// renderContext.lineWidth = 2
		// console.log(renderProperties.width * zoomXViewRatio, renderProperties.height * zoomYViewRatio)
		// renderContext.strokeRect(0, 0, renderProperties.width * zoomXViewRatio, renderProperties.height * zoomYViewRatio)

		zoomView.width = width
		zoomView.height = height
		let x = componentBox.x - 10 - width
		let y = componentBox.y
		zoomView.style.left = x + 'px'
		zoomView.style.top = y + 'px'
		let context = _getContext(zoomView);

		/// offset image based on the mouse position
		let xRatio = currentImage.width / referenceBox.width
		let yRatio = currentImage.height / referenceBox.height
		let invertedXRatio = referenceBox.width / currentImage.width
		let invertedYRatio = referenceBox.height / currentImage.height
		let xPosition = (referenceBox.width * invertedXRatio - e.offsetX) * xRatio
		let yPosition = (referenceBox.height * invertedYRatio - e.offsetY) * yRatio
		let minX = width - currentImage.width
		let minY = height - currentImage.height
		let xStart = xPosition
		let yStart = yPosition
		// make sure that the image is not drawn outside the canvas

		if (xStart < minX) {
			xStart = minX
		}
		if (yStart < minY) {
			yStart = minY
		}
		if (xStart > 0) {
			xStart = 0
		}
		if (yStart > 0) {
			yStart = 0
		}
		context.drawImage(currentImage, xStart, yStart)
	}
	function mouseout(e) {
		isMouseOver = false
		if (!showZoomView) {
			return
		}
	}
	function mouseclick(e) {
		e.preventDefault()
		e.stopPropagation()
	}
</script>

{#if isMouseOver}
	<canvas bind:this={zoomView} id="zoom" class:invisible={!showZoomView} class="absolute box box-shadow rounded-xl" style="pointer-events:none; z-index: 1000"></canvas>
{/if}
<DraggableComponent xAlignment="right" bind:visible={isVisible}>
	<div class="flex flex-col">
		<div class="flex flex-row w-full justify-between items-center toolbar">
			<div class="text-xs select-none text-left pr-2" tooltip="Might impact editor performance, especially with a high quality setting">Autorender</div>
			<input type="checkbox" checked={autoRender} on:change={(e) => (autoRender = e.target.checked)} />
		</div>
		<div class="flex flex-row w-full justify-between items-center toolbar">
			<div class="text-xs select-none text-left pr-2" tooltip="Will be visible in the app regardless of what you choose here.">Tee markers</div>
			<input type="checkbox" checked={showTeeMarkers} on:change={(e) => (showTeeMarkers = e.target.checked)} />
		</div>
		<div class="flex flex-row w-full justify-between items-center toolbar">
			<div class="text-xs select-none text-left pr-2" tooltip="Turning on shadows might be slow depending on your computer.<br /><br /><strong>OBSERVE!</strong><br/>The shadows will always show in the final version<br /> regladless of what you choose here">Shadows</div>
			<input type="checkbox" checked={showShadows} on:change={(e) => (showShadows = e.target.checked)} />
		</div>
		<div class="flex flex-row w-full justify-between items-center toolbar">
			<div class="text-xs select-none text-left pr-2" tooltip="Preview quality (does not impact final rendering)">Quality</div>
			<input class="w-16" tooltip="Image quality. Highest setting might impact performance greatly." type="range" min={minQuality} max={maxQuality} step="1" bind:value={previewQuality} on:change={previewQualityChanged} />
		</div>
		{#if autoRender === false}
			<div class="flex flex-row p-2">
				<button class="btn btn-sm btn-solid-blue w-full" on:click={() => didPressRender()} tooltip="Press to generate new preview image">Generate</button>
			</div>
		{/if}
	</div>
	<ResizeableComponent xAlignment="none" bind:aspectRatio>
		<canvas id="render" class="w-full h-full bg-bigsteel-800" bind:this={renderElement} on:contextmenu={mouseclick} on:mousemove={updateZoomImage} on:mouseover={mouseover} on:mouseout={mouseout} on:blur={mouseout} on:focus={mouseover}></canvas>
	</ResizeableComponent>
	<div class="absolute bottom-0 left-0 p-2 select-none pointer-events-none" style="z-index: 1000">
		{#if currentStatus}
			<div class="text-white text-[8px] bg-black rounded-l px-[6px] py-[3px]">{currentStatus}</div>
		{/if}
	</div>
	<div class="absolute bottom-0 right-0 p-2" style="z-index: 1000">
		<LoadingIndicator hideWhenIdle={true} {isLoading} color="white" />
	</div>
	<!-- {#if showAdvanced}
		<RenderSettings bind:properties={renderProperties} disabled={false} />
	{/if} -->
</DraggableComponent>

<style lang="postcss">
	.toolbar {
		font-size: 14px;
		padding: 5px 10px;
	}
</style>
